import React from 'react';
import moment from 'moment';
import styled from 'styled-components';
import {Activity, TaskActivity} from 'api';

interface TaskType extends Activity {
  resource: TaskActivity;
}

interface Props {
  activity: TaskType;
}

const TodoIcon = () => (
  <BadgeWrapper>
    <svg
      width="30"
      height="30"
      viewBox="0 0 30 30"
      fill="none"
      xmlns="http://www.w3.org/2000/svg">
      <circle cx="15" cy="15" r="15" fill="#F3F5F7" />
      <rect
        x="9"
        y="10.5"
        width="4"
        height="4"
        stroke="var(--text-color-3)"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <rect
        x="9"
        y="17"
        width="4"
        height="2.5"
        stroke="var(--text-color-3)"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <line
        x1="15"
        y1="11.5"
        x2="21"
        y2="11.5"
        stroke="var(--text-color-3)"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <line
        x1="15"
        y1="13.5"
        x2="21"
        y2="13.5"
        stroke="var(--text-color-3)"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <line
        x1="15"
        y1="18"
        x2="21"
        y2="18"
        stroke="var(--text-color-3)"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  </BadgeWrapper>
);

interface StatusBadgeProps {
  status: string;
}

interface LabelBadgeProps {
  label: string;
}

interface PriorityBadgeProps {
  priority: string;
}

const TaskActivityComponent = ({activity}: Props) => {
  const task = activity?.resource?.task;

  return (
    <Container>
      <Summary>
        <TodoIcon />
        <Body>
          <TaskTitle>{task?.title}</TaskTitle>
        </Body>
        <TimeStamp>
          {moment(activity.createdAt).format('YYYY/MM/DD HH:mm')}
        </TimeStamp>
      </Summary>

      {task?.description && (
        <DescriptionContainer>
          <Description
            dangerouslySetInnerHTML={{
              __html: task.description,
            }}
          />
        </DescriptionContainer>
      )}

      <BadgesContainer>
        <StatusBadge status={task?.status}>{task?.statusText}</StatusBadge>

        <LabelBadge label={task?.taskType}>{task?.taskTypeText}</LabelBadge>

        {task?.priority && (
          <PriorityBadge priority={task?.priority}>
            {task?.priority === 'high' ? '優先度高' : 
             task?.priority === 'normal' ? '優先度中' : 
             task?.priority === 'low' ? '優先度低' : task?.priorityText}
          </PriorityBadge>
        )}

        {task?.dueDate && (
          <DueDate>{moment(task?.dueDate).format('YYYY/MM/DD')}</DueDate>
        )}
        {task?.user && (
          <UserName>
            {[task.user.lastName, task.user.firstName]
              .filter(Boolean)
              .join(' ')}
          </UserName>
        )}
      </BadgesContainer>
    </Container>
  );
};

const Container = styled.div`
  margin-left: -30px;
  position: relative;
  padding: 10px 20px 10px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background: #ffffff;
`;

const Summary = styled.div`
  display: flex;
  align-items: center;
  width: 480px;
  height: 30px;
`;

const BadgeWrapper = styled.div`
  margin: auto 16px auto -3px;
  z-index: 1;
  display: flex;
  width: 30px;
  height: 30px;
  align-items: center;
  justify-content: center;
`;

const Body = styled.div`
  margin: auto 0;
  min-width: 0;
  max-width: 100%;
  flex: auto;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TaskTitle = styled.span`
  font-weight: bold;
  font-size: 12px;
  color: var(--text-color-1);
`;

const TimeStamp = styled.span`
  margin: auto 0;
  font-size: 12px;
  color: var(--text-color-2);
`;

const BadgesContainer = styled.div`
  margin-top: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const StatusBadge = styled.div<StatusBadgeProps>`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;
  background-color: ${(props) => {
    switch (props.status) {
      case 'completed':
        return '#f0f9ff';
      case 'in_progress':
        return '#fff7ed';
      default:
        return '#eff6ff';
    }
  }};
  color: ${(props) => {
    switch (props.status) {
      case 'completed':
        return '#166534';
      case 'in_progress':
        return '#ea580c';
      default:
        return '#1e40af';
    }
  }};
`;

const LabelBadge = styled.div<LabelBadgeProps>`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;
  background-color: ${(props) => {
    switch (props.label) {
      case 'call':
        return '#f0fdf4';
      case 'email':
        return '#eff6ff';
      case 'meeting':
        return '#fff7ed';
      default:
        return '#f9fafb';
    }
  }};
  color: ${(props) => {
    switch (props.label) {
      case 'call':
        return '#166534';
      case 'email':
        return '#1e40af';
      case 'meeting':
        return '#ea580c';
      default:
        return '#4b5563';
    }
  }};
`;

const PriorityBadge = styled.div<PriorityBadgeProps>`
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  border: 1px solid;
  background-color: ${(props) => {
    switch (props.priority) {
      case 'high':
        return '#fef2f2';
      case 'normal':
        return '#fff7ed';
      default:
        return '#f0fdf4';
    }
  }};
  color: ${(props) => {
    switch (props.priority) {
      case 'high':
        return '#b91c1c';
      case 'normal':
        return '#ea580c';
      default:
        return '#166534';
    }
  }};
  border-color: ${(props) => {
    switch (props.priority) {
      case 'high':
        return '#fecaca';
      case 'normal':
        return '#fed7aa';
      default:
        return '#bbf7d0';
    }
  }};
`;

const DueDate = styled.span`
  font-size: 12px;
  color: var(--text-color-2);
`;

const UserName = styled.span`
  font-size: 12px;
  color: var(--text-color-2);
`;

const DescriptionContainer = styled.div`
  margin-top: 15px;
`;

const Description = styled.div`
  font-size: 12px;
  line-height: 1.5;
  color: var(--text-color-1);
  word-break: break-word;
`;

export default TaskActivityComponent;
